import React, { useState, useEffect } from 'react';
import { GitBranch, GitPullRequest, Eye, GitCommit, Star, GitFork, AlertCircle, Github } from 'lucide-react';
import WidgetContainer from '../WidgetContainer';

const GITHUB_USERNAME = 'rar-file';

const GitHubActivity = ({ delay = 0 }) => {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchGitHubActivity();
  }, []);

  async function fetchGitHubActivity() {
    try {
      const response = await fetch(`https://api.github.com/users/${GITHUB_USERNAME}/events`);

      if (!response.ok) {
        throw new Error('Failed to fetch GitHub activity');
      }

      const events = await response.json();

      const recentActivity = events
        .map(event => parseEvent(event))
        .filter(activity => activity !== null)
        .slice(0, 20);

      setActivity(recentActivity);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching GitHub activity:', error);
      setError(error.message);
      setLoading(false);
    }
  }

  function parseEvent(event) {
    const { type, repo, created_at, payload } = event;
    const repoName = repo.name;
    const date = getRelativeTime(created_at);

    switch (type) {
      case 'PushEvent': {
        const commitCount = payload.commits?.length || 1;
        const commitMsg = payload.commits?.[0]?.message || 'Push to repository';
        return {
          type: 'commit',
          repo: repoName,
          title: commitCount > 1
            ? `Pushed ${commitCount} commits`
            : commitMsg.split('\n')[0].substring(0, 60),
          date,
        };
      }
      case 'PullRequestEvent':
        return { type: 'pr', repo: repoName, title: payload.pull_request.title, date, action: payload.action };
      case 'PullRequestReviewEvent':
        return { type: 'review', repo: repoName, title: `Reviewed: ${payload.pull_request.title}`, date };
      case 'IssuesEvent':
        return { type: 'issue', repo: repoName, title: `${payload.action === 'opened' ? 'Opened' : 'Commented on'}: ${payload.issue.title}`, date };
      case 'IssueCommentEvent':
        return { type: 'issue', repo: repoName, title: `Commented on: ${payload.issue.title}`, date };
      case 'WatchEvent':
        return { type: 'star', repo: repoName, title: 'Starred repository', date };
      case 'CreateEvent':
        if (payload.ref_type === 'repository') return { type: 'repo', repo: repoName, title: 'Created repository', date };
        return null;
      case 'ForkEvent':
        return { type: 'fork', repo: repoName, title: 'Forked repository', date };
      default:
        return null;
    }
  }

  function getRelativeTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  }

  const getActivityIcon = (type) => {
    switch (type) {
      case 'pr': return <GitPullRequest className="w-4 h-4" />;
      case 'review': return <Eye className="w-4 h-4" />;
      case 'commit': return <GitCommit className="w-4 h-4" />;
      case 'star': return <Star className="w-4 h-4" />;
      case 'fork': return <GitFork className="w-4 h-4" />;
      case 'issue': return <AlertCircle className="w-4 h-4" />;
      default: return <GitBranch className="w-4 h-4" />;
    }
  };

  const Header = () => (
    <div className="widget-header">
      <h3>GitHub</h3>
      <Github className="widget-icon" />
    </div>
  );

  if (loading) {
    return (
      <WidgetContainer delay={delay}>
        <Header />
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="skeleton h-20" />
          <div className="skeleton h-20" />
        </div>
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton h-14" />
          ))}
        </div>
      </WidgetContainer>
    );
  }

  if (error) {
    return (
      <WidgetContainer delay={delay}>
        <Header />
        <div className="flex items-center justify-center h-32">
          <div className="text-coffee-300 text-sm">Failed to load activity</div>
        </div>
      </WidgetContainer>
    );
  }

  const stats = {
    totalPRs: activity.filter(a => a.type === 'pr').length,
    totalReviews: activity.filter(a => a.type === 'review').length,
    totalCommits: activity.filter(a => a.type === 'commit').length,
  };

  return (
    <WidgetContainer delay={delay}>
      <Header />

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-coffee-700/20 hover:bg-coffee-700/40 rounded-lg p-4 border border-coffee-700/30 transition-all duration-300 hover:-translate-y-0.5 hover:border-caramel-400/40">
          <div className="text-3xl font-bold text-cream-100">{stats.totalPRs}</div>
          <div className="text-xs text-coffee-300 uppercase tracking-wider mt-1">PRs</div>
        </div>
        <div className="bg-coffee-700/20 hover:bg-coffee-700/40 rounded-lg p-4 border border-coffee-700/30 transition-all duration-300 hover:-translate-y-0.5 hover:border-caramel-400/40">
          <div className="text-3xl font-bold text-cream-100">{stats.totalCommits}</div>
          <div className="text-xs text-coffee-300 uppercase tracking-wider mt-1">Commits</div>
        </div>
      </div>

      {/* Activity */}
      <div className="space-y-2">
        {activity.slice(0, 8).map((item, index) => (
          <div
            key={index}
            className="flex items-start gap-3 p-3 rounded-lg bg-coffee-700/20 hover:bg-coffee-700/40 border border-transparent hover:border-caramel-400/30 transition-all duration-300 hover:-translate-y-0.5"
          >
            <div className="text-caramel-400 mt-0.5" style={{ filter: 'drop-shadow(0 0 6px rgba(201, 168, 117, 0.5))' }}>
              {getActivityIcon(item.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-cream-100 font-medium truncate">{item.repo}</div>
              <div className="text-xs text-coffee-200 truncate">{item.title}</div>
              <div className="text-xs text-coffee-400 mt-1">{item.date}</div>
            </div>
          </div>
        ))}
      </div>
    </WidgetContainer>
  );
};

export default GitHubActivity;
